// src/components/Social/MyContactsPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import './MyContacts.css';

// ✅ Íconos modernos
import { FiMessageSquare, FiSlash, FiUnlock, FiTrash2 } from 'react-icons/fi';
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;
const HOST = process.env.REACT_APP_HOST || API_URL;

// Normaliza valores: '', 'undefined', 'null' → null
const sanitizeStored = (raw) => {
  if (raw === undefined || raw === null) return null;
  const v = String(raw).trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return null;
  return v;
};

// 🔧 Normaliza la URL de la foto
const resolvePhotoUrl = (raw, role) => {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('/')) return `${HOST}${v}`;
  if (role === 'trainer') return `${HOST}/uploads/trainers/${v}`;
  return `${HOST}/uploads/athletes/${v}`;
};

const MyContactsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userId =
    sanitizeStored(localStorage.getItem('userId')) ||
    sanitizeStored(localStorage.getItem('userID'));

  const myRole = sanitizeStored(localStorage.getItem('userRole')) || 'athlete';

  const defaultPhoto =
    myRole === 'trainer'
      ? `${HOST}/uploads/trainers/default.jpg`
      : `${HOST}/uploads/athletes/default.jpg`;

  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState('');

  // ✅ mostramos ambas listas (Activos + Bloqueados)
  const [activeContacts, setActiveContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);

  // ✅ mutual contacts cache por userId
  const [mutualByUserId, setMutualByUserId] = useState({});
  const mutualRef = useRef({});
  useEffect(() => {
    mutualRef.current = mutualByUserId;
  }, [mutualByUserId]);

  // Helpers
  const isVirtualMatchId = useCallback((matchId) => {
    const s = String(matchId || '');
    return s.startsWith('coach:') || s.startsWith('athlete:');
  }, []);

  // ===========================
  // Mutual contacts (backend)
  // ===========================
  const fetchMutualContacts = useCallback(
    async (targetUserId) => {
      const tid = String(targetUserId || '').trim();
      if (!tid || !userId) return null;

      const cached = mutualRef.current[tid];
      if (cached && cached.loaded) return cached;

      try {
        setMutualByUserId((prev) => ({
          ...prev,
          [tid]: { ...(prev[tid] || {}), loading: true, loaded: false, error: '' },
        }));

        const res = await fetch(
          `${API_URL}/api/community/mutual-contacts?targetUserId=${encodeURIComponent(tid)}`,
          { headers: { 'Content-Type': 'application/json', 'x-user-id': userId } }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const payload = {
          count: Number(data?.count || 0),
          users: Array.isArray(data?.users) ? data.users : [],
          hasMore: !!data?.hasMore,
          loading: false,
          loaded: true,
          error: '',
        };

        setMutualByUserId((prev) => ({ ...prev, [tid]: payload }));
        return payload;
      } catch (e) {
        console.error('[MyContactsPage] mutual-contacts error', e);
        setMutualByUserId((prev) => ({
          ...prev,
          [tid]: {
            count: 0,
            users: [],
            hasMore: false,
            loading: false,
            loaded: true,
            error: 'error',
          },
        }));
        return null;
      }
    },
    [userId]
  );

  const runLimited = useCallback(async (items, limit, fn) => {
    let idx = 0;
    const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
      while (idx < items.length) {
        const i = idx++;
        await fn(items[i]);
      }
    });
    await Promise.all(workers);
  }, []);

  const prefetchMutualForContacts = useCallback(
    async (contacts) => {
      if (!userId) return;

      const ids = Array.from(
        new Set(
          (contacts || [])
            .filter((c) => c?.id && c.contactType === 'community' && !isVirtualMatchId(c.matchId))
            .map((c) => String(c.id))
        )
      );

      if (!ids.length) return;

      await runLimited(ids, 4, fetchMutualContacts);
    },
    [userId, runLimited, fetchMutualContacts, isVirtualMatchId]
  );

  // ===========================
  // Map matches -> cards
  // ===========================
  const mapMatches = useCallback(
    (rawMatches) => {
      const matches = Array.isArray(rawMatches) ? rawMatches : [];

      const mapped = matches
        .map((m) => {
          const matchId = String(m.matchId || m._id || m.id || '');
          const ou = m.otherUser || m.other || m.partner || {};
          const otherId = ou.id || ou.userId || ou._id || '';

          const role = ou.role || ou.userRole || 'athlete';
          const rawPhoto = ou.photoUrl || ou.avatarUrl || ou.profilePhoto || ou.profileImage || '';
          const photoUrl = resolvePhotoUrl(rawPhoto, role) || defaultPhoto;

          const vMatch = isVirtualMatchId(matchId);

          return {
            matchId,
            id: String(otherId),
            name: ou.name || ou.displayName || t('social.partnerTzone'),
            role,
            photoUrl,
            city: ou.city || '',
            country: ou.country || '',
            sports: Array.isArray(ou.disciplines)
              ? ou.disciplines
              : Array.isArray(ou.sports)
              ? ou.sports
              : ou.sports
              ? [ou.sports]
              : [],
            bio: ou.bio || ou.about || '',
            isBlocked: !!(m.isBlocked || ou.isBlocked || m.status === 'blocked'),
            contactType: m.contactType || (vMatch ? 'athlete' : 'community'),
            canChat: typeof m.canChat === 'boolean' ? m.canChat : !vMatch,
            status: m.status || '',
            relationshipTag: m.relationshipTag || ou.relationshipTag || (vMatch ? 'athlete' : 'community'),
            relationshipLabel:
              m.relationshipLabel ||
              ou.relationshipLabel ||
              (vMatch ? t('social.myAthlete') : t('social.community')),
          };
        })
        .filter((c) => c.id && c.matchId);

      const uniq = new Map();
      for (const c of mapped) {
        if (!uniq.has(String(c.id))) uniq.set(String(c.id), c);
      }
      return Array.from(uniq.values());
    },
    [defaultPhoto, isVirtualMatchId, t]
  );

  // ===========================
  // Load contacts
  // ===========================
  const loadContacts = useCallback(async () => {
    if (!userId) {
      setError(t('social.contacts.errors.currentUserNotFound'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const [resActive, resBlocked] = await Promise.all([
        fetch(`${API_URL}/api/community/matches?status=active`, {
          headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        }),
        fetch(`${API_URL}/api/community/matches?status=blocked`, {
          headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        }),
      ]);

      if (!resActive.ok) throw new Error(`HTTP active ${resActive.status}`);
      if (!resBlocked.ok) throw new Error(`HTTP blocked ${resBlocked.status}`);

      const rawActive = await resActive.json();
      const rawBlocked = await resBlocked.json();

      const mappedActive = mapMatches(rawActive);
      const mappedBlocked = mapMatches(rawBlocked);

      setActiveContacts(mappedActive);
      setBlockedContacts(mappedBlocked);

      prefetchMutualForContacts([...mappedActive, ...mappedBlocked]);
    } catch (e) {
      console.error('[MyContactsPage] load error', e);
      setError(t('social.contacts.errors.loadContacts'));
    } finally {
      setLoading(false);
    }
  }, [userId, mapMatches, prefetchMutualForContacts, t]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const title = useMemo(() => {
    if (loading) return t('social.contacts.loadingContacts');
    const total = (activeContacts?.length || 0) + (blockedContacts?.length || 0);
    if (total === 0) return t('social.contacts.noContactsYet');
    return t('social.contacts.myContactsCount', { count: total });
  }, [loading, activeContacts?.length, blockedContacts?.length, t]);

  const goBack = () => navigate('/social');

  const goProfile = (c) => {
    if (!c?.id) return;
    navigate(`/community/profile/${c.id}`);
  };

  const goChat = (c) => {
    navigate('/social/messages', {
      state: { initialMatchId: c.matchId, fromMatch: false },
    });
  };

  const doBlock = async (c) => {
    if (!c?.id || actionBusy) return;
    try {
      setActionBusy(true);
      const res = await fetch(`${API_URL}/api/community/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        body: JSON.stringify({ targetUserId: c.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadContacts();
    } catch (e) {
      console.error('[MyContactsPage] block error', e);
      alert(t('social.contacts.errors.blockUser'));
    } finally {
      setActionBusy(false);
    }
  };

  const doUnblock = async (c) => {
    if (!c?.id || actionBusy) return;
    try {
      setActionBusy(true);
      const res = await fetch(`${API_URL}/api/community/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        body: JSON.stringify({ targetUserId: c.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadContacts();
    } catch (e) {
      console.error('[MyContactsPage] unblock error', e);
      alert(t('social.contacts.errors.unblockUser'));
    } finally {
      setActionBusy(false);
    }
  };

  const doDeleteMatch = async (c) => {
    if (!c?.matchId) return;
    if (isVirtualMatchId(c.matchId)) {
      alert(t('social.contacts.errors.notCommunityMatch'));
      return;
    }
    if (actionBusy) return;

    const ok = window.confirm(
      t('social.contacts.confirmDeleteMatch', { name: c.name })
    );
    if (!ok) return;

    try {
      setActionBusy(true);
      const res = await fetch(
        `${API_URL}/api/community/matches/${encodeURIComponent(c.matchId)}/delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadContacts();
    } catch (e) {
      console.error('[MyContactsPage] delete match error', e);
      alert(t('social.contacts.errors.deleteMatch'));
    } finally {
      setActionBusy(false);
    }
  };

  const renderDeck = (list, sectionTitle) => {
    if (!list || list.length === 0) return null;

    return (
      <>
        <div className="my-contacts-section-title">{sectionTitle}</div>

        <div className="my-contacts-deck" role="list">
          {list.map((c) => {
            const sportsText =
              Array.isArray(c.sports) && c.sports.length
                ? c.sports.join(' · ')
                : t('social.partner');

            const locationText = [c.city, c.country].filter(Boolean).join(', ') || '—';

            const canChat = !c.isBlocked && c.canChat && !isVirtualMatchId(c.matchId);

            const mutual = mutualByUserId[String(c.id)];
            const showMutual =
              c.contactType === 'community' &&
              !isVirtualMatchId(c.matchId) &&
              (mutual?.loading || mutual?.count > 0);

            return (
              <article
                key={c.matchId}
                className={'my-contact-card' + (c.isBlocked ? ' my-contact-card--blocked' : '')}
                role="listitem"
              >
                <div
                  className="my-contact-photo"
                  onClick={() => goProfile(c)}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={c.photoUrl || defaultPhoto} alt={c.name} />
                </div>

                <div className="my-contact-overlay" />

                <div className="my-contact-info">
                  <div className="my-contact-name-row">
                    <h3
                      className="my-contact-name"
                      onClick={() => goProfile(c)}
                      style={{ cursor: 'pointer' }}
                    >
                      {c.name}
                    </h3>

                    <div className="my-contact-badges">
                      <span
                        className={
                          'my-contact-badge my-contact-badge--rel ' +
                          (c.relationshipTag === 'athlete'
                            ? 'my-contact-badge--athlete'
                            : 'my-contact-badge--community')
                        }
                        title={t('social.contacts.relationship')}
                      >
                        {c.relationshipLabel ||
                          (c.relationshipTag === 'athlete'
                            ? t('social.myAthlete')
                            : t('social.community'))}
                      </span>

                      {c.isBlocked && (
                        <span className="my-contact-badge my-contact-badge--blocked">
                          {t('social.contacts.blocked')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="my-contact-sub">{sportsText}</div>
                  <div className="my-contact-sub2">{locationText}</div>

                  {showMutual ? (
                    <div className="my-contact-mutual">
                      {mutual?.loading ? (
                        <span className="my-contact-mutual-text">
                          {t('social.loadingMutualContacts')}
                        </span>
                      ) : mutual?.count > 0 ? (
                        <>
                          <div className="my-contact-mutual-avatars" aria-hidden="true">
                            {(mutual.users || []).slice(0, 3).map((u, idx) => {
                              const uRole = u?.role || 'athlete';
                              const uPhoto =
                                resolvePhotoUrl(u?.photoUrl || u?.profilePhoto || '', uRole) ||
                                defaultPhoto;

                              return (
                                <span
                                  key={`${u?.id || u?.name || idx}`}
                                  className="my-contact-mutual-avatar"
                                  style={{ left: `${idx * 14}px`, zIndex: 10 - idx }}
                                  title={u?.name || t('social.contact')}
                                >
                                  <img src={uPhoto} alt="" />
                                </span>
                              );
                            })}
                          </div>

                          <div className="my-contact-mutual-lines">
                            <div className="my-contact-mutual-line1">
                              {t('social.mutualContacts')}{' '}
                              <span className="my-contact-mutual-dot">•</span>{' '}
                              {t('social.connectedBy')}
                            </div>

                            <div
                              className="my-contact-mutual-line2"
                              title={(mutual.users || [])
                                .map((u) => u?.name)
                                .filter(Boolean)
                                .join(', ')}
                            >
                              {(mutual.users || [])
                                .map((u) => u?.name)
                                .filter(Boolean)
                                .join(` ${t('social.and')} `)}
                              {mutual.hasMore ? '…' : ''}
                            </div>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  {c.bio ? <p className="my-contact-bio">{c.bio}</p> : null}
                </div>

                <div className="my-contact-actions">
                  <button
                    type="button"
                    className="my-contact-action-btn my-contact-action-btn--chat"
                    onClick={() => goChat(c)}
                    disabled={!canChat}
                    aria-label={t('social.contacts.openChat')}
                    title={!canChat ? t('social.contacts.chatUnavailable') : t('social.contacts.openChat')}
                  >
                    <FiMessageSquare size={18} />
                  </button>

                  <button
                    type="button"
                    className="my-contact-action-btn my-contact-action-btn--block"
                    onClick={() => (c.isBlocked ? doUnblock(c) : doBlock(c))}
                    disabled={actionBusy}
                    aria-label={c.isBlocked ? t('social.contacts.unblock') : t('social.contacts.block')}
                    title={c.isBlocked ? t('social.contacts.unblock') : t('social.contacts.block')}
                  >
                    {c.isBlocked ? <FiUnlock size={18} /> : <FiSlash size={18} />}
                  </button>

                  <button
                    type="button"
                    className="my-contact-action-btn my-contact-action-btn--delete"
                    onClick={() => doDeleteMatch(c)}
                    disabled={actionBusy || isVirtualMatchId(c.matchId)}
                    aria-label={t('social.contacts.deleteMatch')}
                    title={
                      isVirtualMatchId(c.matchId)
                        ? t('social.contacts.notCommunityMatchShort')
                        : t('social.contacts.deleteMatch')
                    }
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="my-contacts-root">
      <header className="my-contacts-header">
        <button className="my-contacts-back" type="button" onClick={goBack}>
          ⬅ {t('social.community')}
        </button>
        <h2 className="my-contacts-title">{title}</h2>
      </header>

      {error && <div className="my-contacts-error">{error}</div>}
      {loading && <div className="my-contacts-loading">{t('social.loading')}</div>}

      {!loading && !error && activeContacts.length === 0 && blockedContacts.length === 0 && (
        <div className="my-contacts-empty">
          <p>{t('social.contacts.emptyState')}</p>
          <button className="my-contacts-cta" onClick={() => navigate('/social')}>
            {t('social.searchPartner')}
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          {renderDeck(activeContacts, t('social.contacts.activeCount', { count: activeContacts.length }))}
          {renderDeck(blockedContacts, t('social.contacts.blockedCount', { count: blockedContacts.length }))}
        </>
      )}
    </div>
  );
};

export default MyContactsPage;